import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuthStore, useImportStore } from '../../stores';
import { useSubmissionStore } from '../../stores/submissionStore';
import {
  FileText,
  Database,
  Upload,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  BarChart3,
} from 'lucide-react';

// 快捷操作
const quickActions = [
  { label: '新建上报', path: '/reports/new', icon: FileText, color: 'bg-cyan-500/20 text-cyan-400' },
  { label: '数据导入', path: '/imports', icon: Upload, color: 'bg-green-500/20 text-green-400' },
  { label: '维度管理', path: '/dimensions', icon: Database, color: 'bg-purple-500/20 text-purple-400' },
  { label: '用户管理', path: '/users', icon: Users, color: 'bg-orange-500/20 text-orange-400' },
];

export default function DashboardPage() {
  const { user, hasPermission } = useAuthStore();
  // 使用 selector 只订阅需要的状态，避免不必要的重新渲染
  const submissions = useSubmissionStore(state => state.submissions);
  const fetchSubmissions = useSubmissionStore(state => state.fetchSubmissions);
  const tasks = useImportStore(state => state.tasks);
  const fetchTasks = useImportStore(state => state.fetchTasks);

  const hasLoaded = useRef(false);
  
  // 加载统计数据 - 只加载一次
  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    
    // 加载上报数据
    fetchSubmissions({ pageIndex: 1, pageSize: 5 });
    // 加载导入任务
    fetchTasks({ pageIndex: 1, pageSize: 5 });
  }, [fetchSubmissions, fetchTasks]);

  // 计算统计数据
  const pendingCount = submissions.filter(r => r.status === 'pending').length;
  const approvedCount = submissions.filter(r => r.status === 'approved').length;
  const totalSubmissions = submissions.length;

  const stats = [
    { label: '总上报数', value: totalSubmissions.toString(), icon: FileText, color: 'cyan' },
    { label: '待审核', value: pendingCount.toString(), icon: Clock, color: 'orange' },
    { label: '已通过', value: approvedCount.toString(), icon: CheckCircle, color: 'green' },
    { label: '导入任务', value: tasks.length.toString(), icon: Upload, color: 'purple' },
  ];

  // 最近动态 - 从实际数据生成
  const recentActivities = [
    ...submissions.slice(0, 3).map(r => ({
      id: r.id,
      action: r.status === 'draft' ? '创建了新上报' : r.status === 'pending' ? '提交了上报' : '审核了上报',
      user: r.submitterName,
      time: r.submitTime ? new Date(r.submitTime).toLocaleDateString('zh-CN') : '刚刚',
      type: r.status,
    })),
    ...tasks.slice(0, 2).map(t => ({
      id: `import-${t.id}`,
      action: t.status === 'completed' ? '完成了数据导入' : '开始了数据导入',
      user: t.createdBy || '系统用户',
      time: new Date(t.createdAt).toLocaleDateString('zh-CN'),
      type: 'import',
    })),
  ];

  return (
    <div className="space-y-6">
      {/* 欢迎区域 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">
            欢迎回来，{user?.displayName || user?.username}！
          </h2>
          <p className="text-slate-400 mt-1">
            今天是 {new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
          </p>
        </div>
        <div className="flex gap-2">
          {hasPermission('report:create') && (
            <Button asChild className="bg-cyan-500 hover:bg-cyan-600 text-white">
              <Link to="/reports/new">
                <FileText className="w-4 h-4 mr-2" />
                新建上报
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* 统计卡片 - 从数据库获取 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.label} className="p-4 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400">{stat.label}</p>
                <p className="text-2xl font-bold text-slate-100 mt-1">{stat.value}</p>
              </div>
              <div className={`p-2 rounded-lg bg-${stat.color}-500/20`}>
                <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* 快捷操作 */}
      <Card className="p-6 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">快捷操作</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.label}
              to={action.path}
              className="flex flex-col items-center p-4 rounded-lg border border-slate-700 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all group"
            >
              <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                <action.icon className="w-6 h-6" />
              </div>
              <span className="text-sm font-medium text-slate-300 group-hover:text-cyan-300">{action.label}</span>
            </Link>
          ))}
        </div>
      </Card>

      {/* 内容区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 最近动态 - 从数据库获取 */}
        <Card className="p-6 lg:col-span-2 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-100">最近动态</h3>
            <Button variant="ghost" size="sm" asChild className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10">
              <Link to="/reports">
                查看全部
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
          {recentActivities.length === 0 ? (
            <p className="text-slate-500 text-center py-8">暂无动态</p>
          ) : (
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2" />
                  <div className="flex-1">
                    <p className="text-sm text-slate-200">
                      <span className="font-medium text-cyan-100">{activity.user}</span>
                      {' '}{activity.action}
                    </p>
                    <p className="text-xs text-slate-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* 数据概览 */}
        <Card className="p-6 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-100">数据概览</h3>
            <BarChart3 className="w-5 h-5 text-slate-400" />
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">审核通过率</span>
                <span className="font-medium text-slate-100">
                  {totalSubmissions > 0 ? Math.round((approvedCount / totalSubmissions) * 100) : 0}%
                </span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all" 
                  style={{ width: `${totalSubmissions > 0 ? (approvedCount / totalSubmissions) * 100 : 0}%` }} 
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">待处理任务</span>
                <span className="font-medium text-slate-100">{pendingCount}/{totalSubmissions}</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-orange-500 rounded-full transition-all" 
                  style={{ width: `${totalSubmissions > 0 ? (pendingCount / totalSubmissions) * 100 : 0}%` }} 
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
